import React, { useCallback, useContext, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import {
  loadNotificationTokens,
  buildNotificationRecipient,
  sendNotification,
} from '../../context-api/api-services/Notification.service';
import { SendNotificationModal } from './SendNotificationModal';
import './NotificationsPage.css';

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'kitchen', label: 'Kitchen' },
];

function truncateToken(token) {
  if (!token || token.length <= 24) return token || '—';
  return `${token.slice(0, 12)}…${token.slice(-8)}`;
}

const Notifications = () => {
  const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [accountType, setAccountType] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchTokens = useCallback(async (page = 1, limit = pagination.limit) => {
    setLoading(true);
    setError(null);
    const result = await loadNotificationTokens({
      page,
      limit,
      account_type: accountType || undefined,
      search: search || undefined,
    });
    if (!result.ok) {
      setRows([]);
      setError(result.error || 'Could not load notification users.');
      setLoading(false);
      return;
    }
    setRows(result.data);
    setPagination((prev) => ({
      ...prev,
      ...(result.pagination || {}),
      page: result.pagination?.page ?? page,
      limit: result.pagination?.limit ?? limit,
    }));
    setLoading(false);
  }, [accountType, search, pagination.limit]);

  useEffect(() => {
    setSelectedMenuTab('notifications');
  }, [setSelectedMenuTab]);

  useEffect(() => {
    void fetchTokens(1);
  }, [fetchTokens]);

  const handlePageChange = (_event, newPage) => {
    void fetchTokens(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const nextLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: nextLimit }));
    void fetchTokens(1, nextLimit);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleOpenSend = (row) => {
    setSelectedUser(row);
    setModalOpen(true);
    setToast(null);
  };

  const handleCloseSend = () => {
    if (sending) return;
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleSend = async (notification) => {
    if (!selectedUser) return;

    const built = buildNotificationRecipient(selectedUser);
    if (!built.ok) {
      setToast({ severity: 'error', message: built.error });
      return;
    }

    setSending(true);
    setToast(null);
    const result = await sendNotification({
      recipient: built.recipient,
      notification,
    });
    setSending(false);
    if (!result.ok) {
      setToast({ severity: 'error', message: result.message });
      return;
    }
    setToast({ severity: 'success', message: result.message });
    setModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <div className="notifications-page">
      <div className="notifications-topbar">
        <div>
          <h1 className="notifications-topbar__title">Push Notifications</h1>
          <div className="notifications-topbar__subtitle">
            Registered devices — send push notifications by account type
          </div>
        </div>
        <Tooltip title="Refresh list">
          <IconButton
            onClick={() => void fetchTokens(pagination.page)}
            aria-label="Refresh notification users"
            sx={{ color: '#091057' }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </div>

      {toast ? (
        <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ mb: 2, maxWidth: 960 }}>
          {toast.message}
        </Alert>
      ) : null}

      <Box className="notifications-filters" component="form" onSubmit={handleSearchSubmit}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="notifications-account-type-label">Account type</InputLabel>
          <Select
            labelId="notifications-account-type-label"
            value={accountType}
            label="Account type"
            onChange={(e) => setAccountType(e.target.value)}
          >
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search email, kitchen, device"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
        />
        <button type="submit" className="notifications-filters__search-btn">
          Search
        </button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ maxWidth: 960 }}>
          {error}
        </Alert>
      ) : null}

      <TableContainer className="notifications-table-wrap">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#024CAA' }} />
          </Box>
        ) : (
          <>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Kitchen</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#537D5D' }}>
                      No notification tokens found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row._id || row.device_id} hover>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>
                        {row.account_type ? (
                          <Chip label={row.account_type} size="small" variant="outlined" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{row.kitchen_name || '—'}</TableCell>
                      <TableCell>{row.device_type || '—'}</TableCell>
                      <TableCell>{row.channel_id || 'singlecombo'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.permission ? 'Granted' : 'Denied'}
                          size="small"
                          color={row.permission ? 'success' : 'default'}
                          variant={row.permission ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {truncateToken(row.token)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Send notification">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenSend(row)}
                            aria-label={`Send notification to ${row.email || row.device_id}`}
                            sx={{ color: '#024CAA' }}
                          >
                            <SendRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pagination.total || 0}
              page={Math.max(0, (pagination.page || 1) - 1)}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit || 25}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      <SendNotificationModal
        open={modalOpen}
        user={selectedUser}
        onClose={handleCloseSend}
        onSend={handleSend}
        sending={sending}
      />
    </div>
  );
};

export default Notifications;
